<template>
  <div
    v-if="slides.length"
    class="slideshow-carousel"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
  >
    <div class="slideshow-carousel__container">
      <div class="slideshow-carousel__wrapper">
        <div
          v-for="(_, index) in slides"
          :key="'slide-' + index"
          class="slideshow-item slideshow-carousel__item"
          :class="{ 'active': currentIndex === index }"
        >
          <slot :name="'slide-' + index" />
        </div>
      </div>
    </div>
    <div
      v-if="hasPagination"
      class="slideshow-pagination slideshow-carousel__pagination"
      :class="{'slideshow-pagination--extended': slides.length > 4}"
    >
      <span
        v-for="(_, index) in slides"
        :key="'bullet-' + index"
        :class="[
          'slideshow-pagination__bullet',
          { active: currentIndex === index },
        ]"
        @click="goToSlide(index)"
      />
    </div>
  </div>
</template>

<script>

/**
 * Component: Slideshow Carousel
 * -----------------------------------------------------------------------------
 * A custom carousel component that allows you to display and navigate through
 * slides.
 *
 * Props:
 * @param {Boolean} autoplay - Whether the carousel should autoplay. Default is true.
 * @param {Number}  autoplaySpeed - Interval in milliseconds for autoplay.
 * @param {Boolean} loop - Whether the carousel should loop back to the beginning after the last slide.
 *
 * Slots:
 * -  Named slot: Set unique slot names using the following format - `slide-${index}`.
 *
 * Usage:
 * @example
 * ```vue
 * <slideshow-carousel
 *   id="my-carousel"
 *   :autoplay="true"
 *   :autoplaySpeed="5000"
 *   :loop="true"
 * >
 *   <div class="slide" #['slide-1']>1</div>
 *   <div class="slide" #['slide-2']>2</div>
 *   <div class="slide" #['slide-3']>3</div>
 * </slideshow-carousel>
 * ```
 */
export default {
  name: 'SlideshowCarousel',

  props: {
    autoplay: {
      type: Boolean,
      default: true,
    },
    autoplaySpeed: {
      type: Number,
      default: 3000,
    },
    loop: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      currentIndex: 0,
      autoplayInterval: null,
      touchStartX: 0,
      touchEndX: 0,
      userInteracted: false,
    }
  },

  computed: {

    /**
     * Get the slides from the slots.
     * @returns {Array}
     */
    slides() {
      return Object.keys(this.$slots).filter((slot) =>
        slot.startsWith('slide-'),
      )
    },

    /**
     * Check if there are multiple slides to show pagination.
     * @returns {Boolean}
     */
    hasPagination() {
      return this.slides.length > 1
    },
  },

  mounted() {
    if (this.autoplay) {
      this.startAutoplay()
    }
  },

  beforeUnmount() {
    this.stopAutoplay()
  },

  methods: {

    /**
     * Start the autoplay functionality.
     */
    startAutoplay() {
      if (this.userInteracted || !this.autoplay) {
        return
      }

      this.autoplayInterval = setInterval(() => {
        this.nextSlide()
      }, this.autoplaySpeed)
    },

    /**
     * Stop the autoplay functionality.
     */
    stopAutoplay() {
      clearInterval(this.autoplayInterval)
    },

    /**
     * Move to the next slide in the carousel.
     */
    nextSlide() {
      if (this.currentIndex < this.slides.length - 1) {
        this.currentIndex++
        return
      }

      if (this.loop) {
        this.currentIndex = 0
      }
    },

    /**
     * Move to the previous slide in the carousel.
     */
    prevSlide() {
      if (this.currentIndex > 0) {
        this.currentIndex--
        return
      }

      if (this.loop) {
        this.currentIndex = this.slides.length - 1
      }
    },

    /**
     * Move to a specific slide.
     * @param {Number} index - The index of the slide to go to.
     */
    goToSlide(index) {
      this.currentIndex = index
      this.stopAutoplay()
      this.userInteracted = true
    },

    /**
     * Handle the start of a touch event.
     * @param {Event} event
     */
    handleTouchStart(event) {
      this.touchStartX = event.changedTouches[0].screenX
      this.stopAutoplay()
      this.userInteracted = true
    },

    /**
     * Handle the end of a touch event.
     * @param {Event} event
     */
    handleTouchEnd(event) {
      this.touchEndX = event.changedTouches[0].screenX

      if (this.touchEndX < this.touchStartX) {
        this.nextSlide()
        return
      }

      if (this.touchEndX > this.touchStartX) {
        this.prevSlide()
      }
    },
  },
}
</script>

<style src="./slideshow-carousel.scss" lang="scss"></style>
